import React from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const PrivacyPolicy: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { languageManager } = useLanguage();

    const legal = languageManager.legal;
    const sections = legal.privacy_sections;

    const handleBack = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="min-h-screen bg-[#FBFBFB] flex flex-col font-sans selection:bg-blue-100">
            {/* Navigation */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl py-3`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-14">
                        <div className="flex items-center gap-3 cursor-pointer group" onClick={handleBack}>
                            <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/10 group-hover:scale-105 transition-transform duration-300">
                                <ShieldCheck className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-lg font-bold text-slate-900 tracking-tight">Lashing Calculator</span>
                        </div>
                        <button
                            onClick={handleBack}
                            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-all font-semibold text-sm px-4 py-2 rounded-xl hover:bg-slate-100"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {legal.return_home}
                        </button>
                    </div>
                </div>
            </nav>

            <main className="flex-grow pt-20">
                {/* Hero Section */}
                <div className="bg-slate-900 relative overflow-hidden py-16 sm:py-24">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0 bg-[url('/shipping_background.png')] bg-cover bg-center grayscale"></div>
                        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-transparent to-slate-900"></div>
                    </div>
                    <div className="max-w-4xl mx-auto px-6 relative z-10 text-center sm:text-left">
                        <div className="flex items-center justify-center sm:justify-start gap-2 mb-4 text-slate-400 font-mono text-xs uppercase tracking-[0.2em]">
                            <span>Information</span>
                            <span>/</span>
                            <span>Legal</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
                            {legal.privacy_title}
                        </h1>
                        <div className="flex flex-col sm:flex-row items-center gap-4 text-slate-400 text-sm">
                            <span className="bg-white/10 px-3 py-1 rounded-full border border-white/10 font-medium">
                                {legal.last_updated}
                            </span>
                            <span className="hidden sm:inline opacity-30">|</span>
                            <span className="font-medium">JADE CONSULTANCY AND TRADING LTD</span>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="max-w-4xl mx-auto px-6 py-20 lg:py-32">
                    <div className="space-y-20 lg:space-y-28">
                        {legal.detailed_privacy ? (
                            legal.detailed_privacy.map((section, index) => (
                                <section key={index} className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">{index + 1}.</span>
                                        {section.title.replace(/^\d+\.\s*/, '')}
                                    </h2>
                                    <div className="space-y-6">
                                        {section.content.map((paragraph, pIndex) => (
                                            <p key={pIndex} className="text-[18px] leading-[26px] text-[#575656] font-normal">
                                                {paragraph}
                                            </p>
                                        ))}
                                        {index === 0 && (
                                            <div className="mt-8 p-6 bg-slate-50 border border-slate-200 rounded-2xl flex items-start gap-4">
                                                <div className="w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                                                    <span className="text-white text-[10px] font-bold italic">i</span>
                                                </div>
                                                <p className="text-[16px] text-slate-600 leading-relaxed">
                                                    For details on how we use cookies and tracking technologies, please see our <Link to="/cookies" className="font-bold text-slate-900 underline underline-offset-4 decoration-orange-300 hover:decoration-orange-500 transition-all">Cookie Policy</Link>.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            ))
                        ) : (
                            <div className="space-y-20">
                                {/* Fallback content */}
                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">1.</span> {sections.introduction.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {sections.introduction.content}
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">2.</span> {sections.collection.title}
                                    </h2>
                                    <div className="grid sm:grid-cols-2 gap-12">
                                        <div className="space-y-4">
                                            <h3 className="text-xl font-bold text-slate-900">{sections.collection.personal_title}</h3>
                                            <p className="text-[18px] leading-[26px] text-[#575656]">{sections.collection.personal_desc}</p>
                                        </div>
                                        <div className="space-y-4">
                                            <h3 className="text-xl font-bold text-slate-900">{sections.collection.calc_title}</h3>
                                            <p className="text-[18px] leading-[26px] text-[#575656]">{sections.collection.calc_desc}</p>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">3.</span> {sections.usage.title}
                                    </h2>
                                    <p className="mb-8 text-[18px] leading-[26px] text-[#575656]">
                                        {sections.usage.intro}
                                    </p>
                                    <ul className="space-y-6">
                                        {(sections.usage.items as string[]).map((item, i) => (
                                            <li key={i} className="flex gap-4 items-start">
                                                <div className="w-2 h-2 bg-slate-900 rounded-full mt-2.5 shrink-0" />
                                                <span className="text-[18px] leading-[26px] text-[#575656]">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">4.</span> {sections.compliance.title}
                                    </h2>
                                    <div className="space-y-8">
                                        <p className="text-[18px] leading-[26px] text-[#575656]">
                                            {sections.compliance.content}
                                        </p>
                                        <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl italic">
                                            <p className="text-slate-600 text-[18px] leading-[26px]">
                                                {sections.compliance.note}
                                            </p>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">5.</span> {sections.retention.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {sections.retention.content}
                                    </p>
                                </section>

                                <section className="pt-20 border-t border-slate-200 text-center sm:text-left">
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">6.</span> {sections.contact.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656] mb-10 max-w-2xl">
                                        {sections.contact.content}
                                    </p>
                                    <div className="inline-flex items-center gap-4 bg-slate-900 text-white px-8 py-4 rounded-2xl font-mono text-lg shadow-xl shadow-slate-900/20">
                                        <span className="opacity-50 text-sm">EMAIL:</span>
                                        <span className="font-bold tracking-tight">{sections.contact.email}</span>
                                    </div>
                                </section>
                            </div>
                        )}
                    </div>

                    <div className="mt-28 lg:mt-40 pt-16 border-t border-slate-200 text-center text-slate-400 text-sm uppercase tracking-[0.2em] font-bold">
                        {legal.footer_note}
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-200 py-16 px-6 mt-auto">
                <div className="max-w-4xl mx-auto flex flex-col items-center gap-8 text-center">
                    <div className="flex items-center gap-4 group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/10 group-hover:scale-110 transition-transform">
                            <ShieldCheck className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xl font-bold text-slate-900 tracking-tight">Lashing Calculator</span>
                    </div>
                    <div className="space-y-4 uppercase tracking-[0.3em] text-[10px] font-bold text-slate-400">
                        <p>&copy; {new Date().getFullYear()} Lashing Calculator</p>
                        <p>A service provided by JADE CONSULTANCY AND TRADING LTD</p>
                        <p>All rights reserved</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
